import React from "react";
import { cn } from "../../lib/utils";

const ELLIPSIS = "ellipsis";

function createPageItems({ activePage, totalPages, boundaryRange = 1, siblingRange = 1, hideEllipsis = false }) {
  const items = [];
  if (totalPages <= 0) return items;

  const boundary = Math.max(0, boundaryRange);
  const sibling = Math.max(0, siblingRange);

  const startPages = [];
  for (let i = 1; i <= Math.min(boundary, totalPages); i++) startPages.push(i);

  const endPages = [];
  for (let i = Math.max(totalPages - boundary + 1, boundary + 1); i <= totalPages; i++) endPages.push(i);

  const siblingStart = Math.max(activePage - sibling, boundary + 1);
  const siblingEnd = Math.min(activePage + sibling, totalPages - boundary);

  const middlePages = [];
  for (let i = siblingStart; i <= siblingEnd; i++) {
    if (!startPages.includes(i) && !endPages.includes(i)) middlePages.push(i);
  }

  const allPages = [...new Set([...startPages, ...middlePages, ...endPages])].sort((a, b) => a - b);

  for (let idx = 0; idx < allPages.length; idx++) {
    const page = allPages[idx];
    const prevPage = idx > 0 ? allPages[idx - 1] : 0;
    if (!hideEllipsis && page - prevPage > 1) {
      items.push({ type: ELLIPSIS, value: prevPage + 1 });
    }
    items.push({ type: "page", value: page, active: page === activePage });
  }

  return items;
}

const ELLIPSIS_CHARACTER = "…";

function DawidPagination({
  activePage = 1,
  totalPages = 1,
  completed = [],
  onPageChange,
  getIterationName,
  hideIteration,
  maxLength,
  boundaryRange = 1,
  siblingRange = 1,
  "aria-label": ariaLabel,
}) {
  const items = createPageItems({ activePage, totalPages, boundaryRange, siblingRange, hideEllipsis: true });

  const handleClick = (page) => {
    if (page === activePage) return;
    if (onPageChange) onPageChange(null, { activePage: page });
  };

  return (
    <nav
      role="navigation"
      aria-label={ariaLabel}
      data-currentpage={activePage}
      data-totalpages={totalPages}
      className="flex flex-wrap gap-1"
    >
      {items.map((item) => {
        if (item.type === ELLIPSIS) {
          return (
            <span key={`ellipsis-${item.value}`} className="px-2 py-1 text-sm text-gray-400 flex items-center">
              {ELLIPSIS_CHARACTER}
            </span>
          );
        }

        const { value, active } = item;
        if (hideIteration && hideIteration(value - 1)) return null;

        const isComplete = completed[value - 1];
        let label = getIterationName ? getIterationName(value) : String(value);
        if (maxLength && label.length > maxLength) {
          label = label.substring(0, maxLength) + ELLIPSIS_CHARACTER;
        }

        return (
          <button
            key={`page-${value}`}
            type="button"
            data-page={value}
            onClick={() => handleClick(value)}
            title={getIterationName ? getIterationName(value) : undefined}
            // .btn-page / .btn-page-current geometry. These pagers label loop
            // iterations by name rather than number, so they keep body type and
            // a flexible width instead of the DS's tabular font-display digits.
            className={cn(
              "inline-flex h-8 max-w-[8rem] items-center justify-center truncate rounded-lg border px-2 text-sm transition-colors",
              active
                ? "border-primary-900 bg-primary-900 text-on-primary"
                : isComplete
                ? "border-green-300 bg-green-50 text-green-800 hover:bg-green-100"
                : "border-gray-300 bg-white text-primary-900 hover:border-primary-900 hover:bg-primary-900 hover:text-on-primary"
            )}
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

export { DawidPagination };
